import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useContextMenu } from "../../../../contexts/RightClickMenuContext";
import { useTeamPresence } from "../../../../contexts/TeamPresenceContext";
import { useLayout } from "../../../../contexts/LayoutContext";
import { getThreads } from "../../../../../api/chat";
import {
  getConversations,
  archiveConversation,
  getArchivedConversations,
  muteConversation,
  leaveGroup,
} from "../../../../../api/teamChat";
import { Archive, BellOff, Bell, LogOut, Pencil, Trash2 } from "lucide-react";

const THREAD_FETCH_COOLDOWN_MS = 500;

const makeDedupedFetcher = (fetchFn) => {
  const state = { inFlight: null, lastCompletedAt: 0 };

  return () => {
    if (state.inFlight) return state.inFlight;

    if (Date.now() - state.lastCompletedAt < THREAD_FETCH_COOLDOWN_MS) {
      return Promise.resolve();
    }

    state.inFlight = fetchFn().finally(() => {
      state.lastCompletedAt = Date.now();
      state.inFlight = null;
    });

    return state.inFlight;
  };
};

const getDraftRegistry = () => {
  if (!window.__chatDraftThreads) {
    window.__chatDraftThreads = {};
  }

  return window.__chatDraftThreads;
};

const useChatSidebarData = () => {
  const { currentAppPage } = useLayout();
  const navigate = useNavigate();

  const {
    onlineUsers,
    unreadCounts,
    mutedConversations,
    updateMutedConversation,
  } = useTeamPresence();

  const [threadsByType, setThreadsByType] = useState({
    private: [],
    public: [],
  });

  const [draftsByType, setDraftsByType] = useState({
    private: [],
    public: [],
  });

  const [teamConversations, setTeamConversations] = useState({
    channels: [],
    groups: [],
    dms: [],
  });

  const [archivedIds, setArchivedIds] = useState([]);
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [currentTeamId, setCurrentTeamId] = useState(null);

  const [expandedDirs, setExpandedDirs] = useState({
    private: true,
    public: false,
    team: false,
  });

  const [expandedTeamSubs, setExpandedTeamSubs] = useState({
    groups: true,
    dms: true,
    archived: false,
  });

  const [threadRefreshKey, setThreadRefreshKey] = useState(0);

  const loadThreadsImpl = useCallback(async () => {
    try {
      const [privateRes, publicRes] = await Promise.all([
        getThreads(0, "private"),
        getThreads(0, "public"),
      ]);

      setThreadsByType({
        private: privateRes?.data?.threads || [],
        public: publicRes?.data?.threads || [],
      });
    } catch (error) {
      console.error("Error loading chat threads:", error);
      setThreadsByType({ private: [], public: [] });
    }

    setThreadRefreshKey((prev) => prev + 1);
  }, []);

  const loadTeamConversationsImpl = useCallback(async () => {
    try {
      const res = await getConversations();
      const data = res?.data || {};

      setTeamConversations({
        channels: data.channels || [],
        groups: data.groups || [],
        dms: data.dms || [],
      });
    } catch (error) {
      console.error("Error loading team conversations:", error);
      setTeamConversations({ channels: [], groups: [], dms: [] });
    }
  }, []);

  const loadThreadsRef = useRef(null);
  const loadTeamConversationsRef = useRef(null);

  if (!loadThreadsRef.current) {
    loadThreadsRef.current = makeDedupedFetcher(loadThreadsImpl);
  }

  if (!loadTeamConversationsRef.current) {
    loadTeamConversationsRef.current = makeDedupedFetcher(
      loadTeamConversationsImpl,
    );
  }

  useEffect(() => {
    loadThreadsRef.current = makeDedupedFetcher(loadThreadsImpl);
  }, [loadThreadsImpl]);

  useEffect(() => {
    loadTeamConversationsRef.current = makeDedupedFetcher(
      loadTeamConversationsImpl,
    );
  }, [loadTeamConversationsImpl]);

  const loadThreads = useCallback(() => loadThreadsRef.current(), []);

  const loadTeamConversations = useCallback(
    () => loadTeamConversationsRef.current(),
    [],
  );

  const mappedByType = useMemo(() => {
    const privateServer = threadsByType.private.map((thread) => ({
      id: thread.thread_id,
      label: thread.title || "Thread",
      thread_type: thread.thread_type || "private",
      isLocal: false,
      status: "ready",
      loading: false,
    }));

    const publicServer = threadsByType.public.map((thread) => ({
      id: thread.thread_id,
      label: thread.title || "Thread",
      thread_type: "public",
      isLocal: false,
      status: "ready",
      loading: false,
    }));

    const merge = (drafts, server) => {
      const draftIds = new Set(drafts.map((thread) => thread.id));

      return [
        ...drafts,
        ...server.filter((thread) => !draftIds.has(thread.id)),
      ];
    };

    return {
      private: merge(draftsByType.private, privateServer),
      public: merge(draftsByType.public, publicServer),
    };
  }, [draftsByType, threadsByType]);

  const discardDraft = useCallback(
    (threadId, threadType) => {
      if (!threadId || !threadType) return;

      const draft = draftsByType[threadType]?.find(
        (thread) => thread.id === threadId && thread.isLocal,
      );

      if (!draft) return;

      // Only truly empty drafts should disappear when the user clicks away.
      // Once submitted/creating/persisted, keep the shadow row until getThreads
      // returns the server copy.
      if (draft.status !== "draft") return;

      delete getDraftRegistry()[threadId];

      setDraftsByType((prev) => ({
        ...prev,
        [threadType]: (prev[threadType] || []).filter(
          (thread) => thread.id !== threadId,
        ),
      }));

      window.dispatchEvent(
        new CustomEvent("chat:discard-draft-thread", {
          detail: { threadId, thread_type: threadType },
        }),
      );
    },
    [draftsByType],
  );

  const getCurrentDraftType = useCallback(() => {
    if (!currentThreadId) return null;

    if (
      draftsByType.private.some(
        (thread) => thread.id === currentThreadId && thread.isLocal,
      )
    ) {
      return "private";
    }

    if (
      draftsByType.public.some(
        (thread) => thread.id === currentThreadId && thread.isLocal,
      )
    ) {
      return "public";
    }

    return null;
  }, [currentThreadId, draftsByType]);

  const discardCurrentDraftIfChanging = useCallback(
    (nextThreadId = null) => {
      const currentDraftType = getCurrentDraftType();

      if (!currentDraftType) return;
      if (currentThreadId === nextThreadId) return;

      discardDraft(currentThreadId, currentDraftType);
    },
    [currentThreadId, discardDraft, getCurrentDraftType],
  );

  const selectChatThread = useCallback(
    ({ threadId, thread_type = "private" }) => {
      setCurrentThreadId(threadId);
      setCurrentTeamId(null);

      window.dispatchEvent(
        new CustomEvent("sidebar:active-section-changed", {
          detail: { section: "chat" },
        }),
      );

      if (currentAppPage !== "/chat") {
        window.__pendingChatSelection = { threadId, thread_type };
        navigate("/chat");
        return;
      }

      window.dispatchEvent(
        new CustomEvent("sidebar:chat-thread-selected", {
          detail: { threadId, thread_type },
        }),
      );
    },
    [currentAppPage, navigate],
  );

  useEffect(() => {
    loadThreads();
    loadTeamConversations();
  }, [loadThreads, loadTeamConversations]);

  useEffect(() => {
    getArchivedConversations()
      .then((res) => setArchivedIds(res?.data?.archived_conversations || []))
      .catch((error) => console.error("Failed to load archived:", error));
  }, []);

  useEffect(() => {
    if (currentAppPage === "/chat") return;

    discardCurrentDraftIfChanging(null);
    setCurrentThreadId(null);
    setCurrentTeamId(null);
  }, [currentAppPage, discardCurrentDraftIfChanging]);

  useEffect(() => {
    const handler = (event) => {
      if (event.detail?.section && event.detail.section !== "chat") {
        discardCurrentDraftIfChanging(null);
        setCurrentThreadId(null);
        setCurrentTeamId(null);
      }
    };

    window.addEventListener("sidebar:active-section-changed", handler);

    return () =>
      window.removeEventListener("sidebar:active-section-changed", handler);
  }, [discardCurrentDraftIfChanging]);

  useEffect(() => {
    const handler = () => {
      loadThreads();
      loadTeamConversations();
    };

    window.addEventListener("sidebar:refresh-threads", handler);

    return () => window.removeEventListener("sidebar:refresh-threads", handler);
  }, [loadThreads, loadTeamConversations]);

  useEffect(() => {
    const handler = (event) => {
      if (event.detail?.threadId !== undefined) {
        setCurrentThreadId(event.detail.threadId);
      }

      if (event.detail?.teamId !== undefined) {
        setCurrentTeamId(event.detail.teamId);
      }
    };

    window.addEventListener("sidebar:select-thread", handler);

    return () => window.removeEventListener("sidebar:select-thread", handler);
  }, []);

  useEffect(() => {
    const handler = () => loadTeamConversations();

    window.addEventListener("sidebar:refresh-team-conversations", handler);

    return () =>
      window.removeEventListener("sidebar:refresh-team-conversations", handler);
  }, [loadTeamConversations]);

  useEffect(() => {
    const onDraftSubmitted = (event) => {
      const { threadId, thread_type = "private" } = event.detail || {};
      if (!threadId) return;

      setDraftsByType((prev) => ({
        ...prev,
        [thread_type]: (prev[thread_type] || []).map((thread) =>
          thread.id === threadId
            ? {
                ...thread,
                status: "submitted",
                loading: true,
              }
            : thread,
        ),
      }));
    };

    window.addEventListener("chat:draft-thread-submitted", onDraftSubmitted);

    return () => {
      window.removeEventListener(
        "chat:draft-thread-submitted",
        onDraftSubmitted,
      );
    };
  }, []);

  useEffect(() => {
    const onTitleUpdated = (event) => {
      const { threadId, title, thread_type = "private" } = event.detail || {};
      if (!threadId || !title) return;

      setDraftsByType((prev) => ({
        ...prev,
        [thread_type]: (prev[thread_type] || []).map((thread) =>
          thread.id === threadId
            ? {
                ...thread,
                label: title,
                loading: false,
                status: "ready",
              }
            : thread,
        ),
      }));
    };

    const onPersisted = (event) => {
      const { threadId, thread_type } = event.detail || {};
      if (!threadId) return;

      // Important: do NOT remove the local shadow row here.
      // getThreads can lag behind createThread, and removing immediately causes
      // the sidebar row to disappear until a manual refresh.
      setDraftsByType((prev) => {
        const markPersisted = (threads) =>
          threads.map((thread) =>
            thread.id === threadId
              ? {
                  ...thread,
                  status: "persisted",
                  loading: false,
                  thread_type: thread.thread_type || thread_type || "private",
                }
              : thread,
          );

        return {
          private: markPersisted(prev.private),
          public: markPersisted(prev.public),
        };
      });

      loadThreads();
    };

    window.addEventListener("chat:thread-title-updated", onTitleUpdated);
    window.addEventListener("chat:thread-persisted", onPersisted);

    return () => {
      window.removeEventListener("chat:thread-title-updated", onTitleUpdated);
      window.removeEventListener("chat:thread-persisted", onPersisted);
    };
  }, [loadThreads]);

  useEffect(() => {
    const privateServerIds = new Set(
      threadsByType.private.map((thread) => thread.thread_id).filter(Boolean),
    );

    const publicServerIds = new Set(
      threadsByType.public.map((thread) => thread.thread_id).filter(Boolean),
    );

    setDraftsByType((prev) => {
      let changed = false;

      const nextPrivate = prev.private.filter((thread) => {
        const shouldRemove = privateServerIds.has(thread.id);

        if (shouldRemove) {
          changed = true;
          delete getDraftRegistry()[thread.id];
        }

        return !shouldRemove;
      });

      const nextPublic = prev.public.filter((thread) => {
        const shouldRemove = publicServerIds.has(thread.id);

        if (shouldRemove) {
          changed = true;
          delete getDraftRegistry()[thread.id];
        }

        return !shouldRemove;
      });

      if (!changed) return prev;

      return {
        private: nextPrivate,
        public: nextPublic,
      };
    });
  }, [threadsByType]);

  useContextMenu("team-conversation", [
    {
      label: "Mute",
      icon: BellOff,
      action: async (payload) => {
        if (!payload?.team_id) return;

        const isMuted = mutedConversations.includes(payload.team_id);
        await muteConversation(payload.team_id, !isMuted);
        updateMutedConversation(payload.team_id, !isMuted);
      },
      getLabel: (payload) =>
        mutedConversations.includes(payload?.team_id) ? "Unmute" : "Mute",
      getIcon: (payload) =>
        mutedConversations.includes(payload?.team_id) ? Bell : BellOff,
    },
    {
      label: "Archive",
      icon: Archive,
      action: async (payload) => {
        if (!payload?.team_id) return;

        const isArchived = archivedIds.includes(payload.team_id);
        await archiveConversation(payload.team_id, !isArchived);

        setArchivedIds((prev) =>
          isArchived
            ? prev.filter((id) => id !== payload.team_id)
            : [...prev, payload.team_id],
        );
      },
      getLabel: (payload) =>
        archivedIds.includes(payload?.team_id) ? "Unarchive" : "Archive",
    },
    {
      label: "Leave Group",
      icon: LogOut,
      variant: "danger",
      action: async (payload) => {
        if (!payload?.team_id) return;

        try {
          await leaveGroup(payload.team_id);
          loadTeamConversations();

          if (currentTeamId === payload.team_id) {
            setCurrentTeamId(null);
          }
        } catch (error) {
          console.error("Failed to leave group:", error);
        }
      },
      hidden: (payload) =>
        payload?.type === "channel" || payload?.type === "dm",
    },
  ]);

  useContextMenu("chat-thread", [
    {
      label: "Rename",
      icon: Pencil,
      action: (payload) => {
        if (!payload?.thread_id || payload?.isLocal) return;

        window.dispatchEvent(
          new CustomEvent("chat-thread:start-rename", {
            detail: { threadId: payload.thread_id },
          }),
        );
      },
      hidden: (payload) => !!payload?.isLocal,
    },
    {
      label: "Delete",
      icon: Trash2,
      variant: "danger",
      action: (payload) => {
        if (!payload?.thread_id || payload?.isLocal) return;

        window.dispatchEvent(
          new CustomEvent("chat-thread:start-delete", {
            detail: { threadId: payload.thread_id },
          }),
        );
      },
      hidden: (payload) => !!payload?.isLocal,
    },
  ]);

  const allTeamMapped = useMemo(() => {
    const items = [];

    for (const channel of teamConversations.channels) {
      items.push({
        id: channel.team_id,
        label: channel.name || "Channel",
        thread_type: "team",
        isTeam: true,
      });
    }

    for (const group of teamConversations.groups) {
      items.push({
        id: group.team_id,
        label: group.name || "Group",
        thread_type: "team",
        isTeam: true,
      });
    }

    for (const dm of teamConversations.dms) {
      items.push({
        id: dm.team_id,
        label: dm.dm_display_name || "DM",
        thread_type: "team",
        isTeam: true,
      });
    }

    return items;
  }, [teamConversations]);

  const allMapped = useMemo(
    () => [...mappedByType.private, ...mappedByType.public, ...allTeamMapped],
    [mappedByType, allTeamMapped],
  );

  const emitChatBreadcrumb = useCallback(() => {
    if (currentAppPage !== "/chat") return;

    const activeId = currentTeamId || currentThreadId;
    const activeThread = allMapped.find((thread) => thread.id === activeId);

    window.dispatchEvent(
      new CustomEvent("breadcrumb:update", {
        detail: {
          section: "/chat",
          subLabel: activeThread?.label || null,
          subIcon: null,
          hasAdd: true,
          addKind: "chat",
          addLabel: null,
        },
      }),
    );
  }, [currentThreadId, currentTeamId, allMapped, currentAppPage]);

  useEffect(() => {
    emitChatBreadcrumb();
  }, [emitChatBreadcrumb]);

  useEffect(() => {
    const handler = (event) => {
      if (event.detail?.section === "/chat") emitChatBreadcrumb();
    };

    window.addEventListener("breadcrumb:request", handler);

    return () => window.removeEventListener("breadcrumb:request", handler);
  }, [emitChatBreadcrumb]);

  useEffect(() => {
    const handler = (event) => {
      if (event.detail?.section !== "/chat") return;

      discardCurrentDraftIfChanging(null);

      setCurrentThreadId(null);
      setCurrentTeamId(null);

      window.dispatchEvent(
        new CustomEvent("sidebar:chat-thread-selected", {
          detail: { threadId: null, thread_type: "private" },
        }),
      );

      window.dispatchEvent(
        new CustomEvent("sidebar:active-section-changed", {
          detail: { section: "chat" },
        }),
      );

      if (currentAppPage !== "/chat") navigate("/chat");
    };

    window.addEventListener("breadcrumb:add", handler);

    return () => window.removeEventListener("breadcrumb:add", handler);
  }, [currentAppPage, discardCurrentDraftIfChanging, navigate]);

  const handleSelect = useCallback(
    (threadId, thread_type = "private") => {
      discardCurrentDraftIfChanging(threadId);
      selectChatThread({ threadId, thread_type });
    },
    [discardCurrentDraftIfChanging, selectChatThread],
  );

  const handleSelectTeam = useCallback(
    (teamId) => {
      discardCurrentDraftIfChanging(null);

      setCurrentTeamId(teamId);
      setCurrentThreadId(null);

      window.dispatchEvent(
        new CustomEvent("sidebar:active-section-changed", {
          detail: { section: "chat" },
        }),
      );

      if (currentAppPage !== "/chat") {
        window.__pendingChatSelection = {
          threadId: null,
          thread_type: "team",
          team_id: teamId,
        };

        navigate("/chat");
        return;
      }

      window.dispatchEvent(
        new CustomEvent("sidebar:chat-thread-selected", {
          detail: { threadId: null, thread_type: "team", team_id: teamId },
        }),
      );
    },
    [currentAppPage, discardCurrentDraftIfChanging, navigate],
  );

  const handleNew = useCallback(
    (thread_type = "private") => {
      if (thread_type === "team") return;

      const currentDraftType = getCurrentDraftType();

      if (currentDraftType && currentDraftType !== thread_type) {
        discardDraft(currentThreadId, currentDraftType);
      }

      const existingDraft = draftsByType[thread_type]?.find(
        (thread) => thread.status === "draft",
      );

      const draft =
        existingDraft ||
        (() => {
          const id = crypto.randomUUID();

          const nextDraft = {
            id,
            label: "New thread",
            thread_type,
            isLocal: true,
            loading: false,
            status: "draft",
          };

          getDraftRegistry()[id] = nextDraft;

          setDraftsByType((prev) => ({
            ...prev,
            [thread_type]: [nextDraft, ...(prev[thread_type] || [])],
          }));

          window.dispatchEvent(
            new CustomEvent("chat:draft-thread-created", {
              detail: {
                threadId: id,
                thread_type,
                label: "New thread",
              },
            }),
          );

          return nextDraft;
        })();

      setExpandedDirs((prev) => ({
        ...prev,
        [thread_type]: true,
      }));

      selectChatThread({
        threadId: draft.id,
        thread_type,
      });
    },
    [
      currentThreadId,
      discardDraft,
      draftsByType,
      getCurrentDraftType,
      selectChatThread,
    ],
  );

  const toggleDir = useCallback((key) => {
    setExpandedDirs((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const toggleTeamSub = useCallback((key) => {
    setExpandedTeamSubs((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  return {
    onlineUsers,
    unreadCounts,
    mutedConversations,
    mappedByType,
    teamConversations,
    archivedIds,
    currentThreadId,
    currentTeamId,
    expandedDirs,
    expandedTeamSubs,
    threadRefreshKey,
    handleSelect,
    handleSelectTeam,
    handleNew,
    toggleDir,
    toggleTeamSub,
    loadTeamConversations,
    loadThreads,
  };
};

export default useChatSidebarData;
